/**
 * Content schema for "Panic at the Closing!".
 *
 * Purpose
 *   The single source of truth for the SHAPE of game content. Scenarios are authored as
 *   JSON (extracted from `reference/Panic_At_The_Closing.md`) and validated against these
 *   Zod schemas in CI and at load time. TypeScript types are *inferred* via `z.infer` —
 *   never hand-written separately.
 *
 * Key collaborators
 *   - scripts/extract-scenarios.ts  produces JSON that must validate here
 *   - apps/web                      consumes the inferred types + the parsed content
 *   - ./tiers.ts                    presentation metadata for OptionTier (icon/label/rank)
 *
 * Model
 *   A game = `meta` + an ordered catalogue of `scenarios` (a discriminated union on `kind`:
 *   "choice" | "knowledge_check" | "popup") + graded `endings`. Scoring is multi-dimensional:
 *   every option carries a per-dimension `ScoreDelta` across the four performance metrics.
 *   See docs/game-design.md for the design rationale.
 */
import { z } from "zod";

/** The four performance metrics, scored independently across a run. */
export const DIMENSION_IDS = [
  "dealSecurity",
  "clientSatisfaction",
  "counterpartyCooperation",
  "transactionProcess",
] as const;
export const DimensionId = z.enum(DIMENSION_IDS);
export type DimensionId = z.infer<typeof DimensionId>;

/** A per-dimension score change applied when an option is chosen. */
export const ScoreDelta = z.object({
  dealSecurity: z.int(),
  clientSatisfaction: z.int(),
  counterpartyCooperation: z.int(),
  transactionProcess: z.int(),
});
export type ScoreDelta = z.infer<typeof ScoreDelta>;

/**
 * Option quality tier — semantic, never "red"/"green". The UI MUST pair the tier with an
 * icon + text label (WCAG 1.4.1); see ./tiers.ts and docs/brand.md.
 * Source mapping: best = GREEN, good = YELLOW, weak = ORANGE, worst = RED.
 */
export const OptionTier = z.enum(["best", "good", "weak", "worst"]);
export type OptionTier = z.infer<typeof OptionTier>;

/** Who delivers the reaction to the player's choice. */
export const ReactionSource = z.enum([
  "bank",
  "sponsor_counsel",
  "existing_lender",
  "swedish_counsel",
  "other",
]);
export type ReactionSource = z.infer<typeof ReactionSource>;

/** Deal stage. The source labels two sections (Signing, Closing); `post_closing` is
 *  reserved for perfection-step scenarios if we split them out (see docs/game-design.md). */
export const Phase = z.enum(["signing", "closing", "post_closing"]);
export type Phase = z.infer<typeof Phase>;

/** One selectable option within a choice / knowledge-check scenario. */
export const Choice = z.object({
  id: z.string(), // stable id, e.g. "SIGN-01-A"
  label: z.string(), // source letter as authored: "A".."D"
  tier: OptionTier,
  text: z.string(),
  reaction: z.string(),
  reactionSource: ReactionSource.default("bank"),
  /** Effect on the four meters. */
  score: ScoreDelta,
  /** One-line transferable principle shown in feedback / debrief (authored). */
  principle: z.string().optional(),
});
export type Choice = z.infer<typeof Choice>;

/** Standard multiple-choice scenario: description + issue + ranked options. */
export const ChoiceScenario = z.object({
  kind: z.literal("choice"),
  id: z.string(),
  phase: Phase,
  order: z.int(), // position within the phase
  title: z.string().optional(),
  description: z.string(),
  issue: z.string(),
  choices: z.array(Choice).min(2),
  /** Deal-critical scenarios amplify score swings and can gate the final outcome. */
  dealCritical: z.boolean().default(false),
  tags: z.array(z.string()).default([]),
});
export type ChoiceScenario = z.infer<typeof ChoiceScenario>;

/** Concept check (e.g. "What does escrow mean?"). No `issue`; tiers may repeat. */
export const KnowledgeCheckScenario = z.object({
  kind: z.literal("knowledge_check"),
  id: z.string(),
  phase: Phase,
  order: z.int(),
  title: z.string(),
  description: z.string(),
  question: z.string().optional(),
  choices: z.array(Choice).min(2),
  tags: z.array(z.string()).default([]),
});
export type KnowledgeCheckScenario = z.infer<typeof KnowledgeCheckScenario>;

/** One task to be ranked in the prioritisation pop-up. */
export const PopupTask = z.object({
  id: z.string(), // "A".."F"
  text: z.string(),
  /** Accepted priority band [min, max], 1 = first. Some tasks legitimately share a band. */
  correctBand: z.tuple([z.int(), z.int()]),
});
export type PopupTask = z.infer<typeof PopupTask>;

/** The Closing #7 prioritisation pop-up — the deliberate "panic" spike. */
export const PopupScenario = z.object({
  kind: z.literal("popup"),
  id: z.string(),
  phase: Phase,
  order: z.int(),
  title: z.string(),
  description: z.string(),
  prompt: z.string(),
  /** If set, this pop-up fires as a BLOCKING interrupt tied to its host scenario, and must be completed
   *  before play continues. `timing` controls when:
   *    - "on-enter" / "before-answer" — fires BEFORE the host's options (a true mid-task interrupt);
   *    - "after-answer" — fires AFTER the player has answered the host (so a task that references the
   *      host's situation only appears once that situation has actually been on screen).
   *  If absent, it plays standalone in `order`. */
  interrupt: z
    .object({
      hostScenarioId: z.string(),
      // Default to the original mid-task interrupt; CLOSE-09 opts into "after-answer" explicitly.
      timing: z.enum(["on-enter", "before-answer", "after-answer"]).default("before-answer"),
    })
    .optional(),
  tasks: z.array(PopupTask).min(2),
  scoring: z.object({
    method: z.literal("band-distance"),
    /** Awarded when every task lands within its accepted band. */
    perfect: ScoreDelta,
    /** Subtracted per rank-position a task is placed outside its band. */
    penaltyPerRankOffBand: ScoreDelta,
  }),
  debrief: z.string().optional(),
  hook: z
    .object({
      presentation: z.string().default("modal-overlay"),
      incomingChime: z.boolean().default(true),
      emphasizeClock: z.boolean().default(true),
    })
    .optional(),
  tags: z.array(z.string()).default([]),
});
export type PopupScenario = z.infer<typeof PopupScenario>;

/** A scenario is exactly one of these, discriminated on `kind`. */
export const Scenario = z.discriminatedUnion("kind", [
  ChoiceScenario,
  KnowledgeCheckScenario,
  PopupScenario,
]);
export type Scenario = z.infer<typeof Scenario>;

/** A single comparison used by an ending predicate. */
export const Comparison = z.object({
  dimension: DimensionId,
  op: z.enum(["gte", "lte", "gt", "lt"]),
  value: z.int(),
});
export type Comparison = z.infer<typeof Comparison>;

/** A graded ending. The first ending whose predicate matches (in array order) wins. */
export const Ending = z.object({
  id: z.string(),
  label: z.string(),
  /** All comparisons must hold. Empty `all` = always matches → use as the final fallback. */
  predicate: z.object({ all: z.array(Comparison).default([]) }),
  title: z.string(),
  body: z.string(),
});
export type Ending = z.infer<typeof Ending>;

/** A scored performance dimension, as presented in the HUD. */
export const Dimension = z.object({
  id: DimensionId,
  label: z.string(),
  start: z.int().default(60),
  min: z.int().default(0),
  max: z.int().default(100),
});
export type Dimension = z.infer<typeof Dimension>;

/** The complete game package: config + scenarios + endings. */
export const GameContent = z.object({
  version: z.string(),
  meta: z.object({
    title: z.string().default("Panic at the Closing!"),
    timeLimitSeconds: z.int().default(2700), // 45 minutes
    cutoffLabel: z.string().default("15:00 Helsinki"),
    untimedPracticeMode: z.boolean().default(true),
    dimensions: z.array(Dimension).length(4),
  }),
  scenarios: z.array(Scenario),
  endings: z.array(Ending).default([]),
});
export type GameContent = z.infer<typeof GameContent>;
